<template>
  <input
    v-model="inputValue"
    type="number"
    class="h-11 min-w-[3rem] rounded-md border-brd text-base font-medium transition hover:bg-bgr-100 focus:text-txt-stronger focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-thm-hover active:bg-bgr-50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-bgr-50"
    autocomplete="off"
    :min
    :max
    :name
    :step
    :placeholder
    :disabled="disabled || loading"
    @keydown.down="handleArrowDown"
    @keydown.up="handleArrowUp"
    @input="handleInput"
  />
</template>

<script setup lang="ts">
import { clamp } from 'lodash-es'

type InputValue = number | ''

const props = withDefaults(
  defineProps<{
    name: string
    step?: number
    min?: number
    max?: number
    defaultValue?: number
    placeholder?: string
    float?: boolean
    debounce?: number
    disabled?: boolean
    loading?: boolean
  }>(),
  {
    step: 1,
    min: Number.MIN_SAFE_INTEGER,
    max: Number.MAX_SAFE_INTEGER,
    defaultValue: 0,
    placeholder: undefined,
    float: false,
    debounce: 0,
    disabled: false,
  },
)

const model = defineModel<number>({ required: true })

const emit = defineEmits<{ change: [value: number] }>()

const inputValue = ref<InputValue>(props.defaultValue)

watch(model, updateInputValue, { immediate: true })

const handleArrowDown = handleKeydownIfEmpty(() => props.min)
const handleArrowUp = handleKeydownIfEmpty(() => props.max)

function handleKeydownIfEmpty(limit: MaybeRefOrGetter<number>) {
  return (event: KeyboardEvent) => {
    if (inputValue.value !== '') return
    if (props.defaultValue === toValue(limit)) return event.preventDefault()

    inputValue.value = props.defaultValue
  }
}

const handleInput = useDebounceFn(() => {
  const value = normalize(inputValue.value)
  model.value = value
  updateInputValue(value)
  emit('change', value)
}, props.debounce)

function updateInputValue(value: number) {
  inputValue.value = value === props.defaultValue ? '' : value
}

function normalize(value: InputValue): number {
  return clamp(
    value === '' ? props.defaultValue : props.float ? value : Math.round(value),
    props.min,
    props.max,
  )
}
</script>
